import React from 'react';

type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'bold'; children: InlineNode[] };

type BlockNode =
  | { type: 'heading'; level: number; children: InlineNode[] }
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'blockquote'; children: InlineNode[] }
  | { type: 'list'; ordered: boolean; items: InlineNode[][] }
  | { type: 'table'; headers: InlineNode[][]; rows: InlineNode[][][] }
  | { type: 'break' };

function parseInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let last = 0;
  let match: RegExpExecArray | null = regex.exec(text);
  while (match) {
    if (match.index > last) {
      nodes.push({ type: 'text', value: text.slice(last, match.index) });
    }
    nodes.push({
      type: 'bold',
      children: [{ type: 'text', value: match[1] }],
    });
    last = match.index + match[0].length;
    match = regex.exec(text);
  }
  if (last < text.length) {
    nodes.push({ type: 'text', value: text.slice(last) });
  }
  return nodes.length > 0 ? nodes : [{ type: 'text', value: '' }];
}

function renderInline(nodes: InlineNode[], keyPrefix: string): React.ReactNode {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    if (node.type === 'bold') {
      return <strong key={key}>{renderInline(node.children, key)}</strong>;
    }
    return <React.Fragment key={key}>{node.value}</React.Fragment>;
  });
}

function parseTableRow(line: string): string[] {
  return line
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
}

function isTableSeparator(line: string): boolean {
  return /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim());
}

function parseBlocks(markdown: string): BlockNode[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: BlockNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      blocks.push({
        type: 'heading',
        level: heading[1].length,
        children: parseInline(heading[2]),
      });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('> ')) {
      blocks.push({
        type: 'blockquote',
        children: parseInline(trimmed.replace(/^>\s?/, '')),
      });
      i += 1;
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed) || /^[-*]\s+/.test(trimmed)) {
      const ordered = /^\d+\.\s+/.test(trimmed);
      const items: InlineNode[][] = [];
      while (i < lines.length) {
        const current = lines[i].trim();
        const itemMatch = ordered
          ? current.match(/^\d+\.\s+(.*)$/)
          : current.match(/^[-*]\s+(.*)$/);
        if (!itemMatch) {
          break;
        }
        items.push(parseInline(itemMatch[1]));
        i += 1;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    if (trimmed.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const headers = parseTableRow(trimmed).map((cell) => parseInline(cell));
      const rows: InlineNode[][][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim().includes('|')) {
        rows.push(parseTableRow(lines[i].trim()).map((cell) => parseInline(cell)));
        i += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }

    const paragraphLines = [trimmed];
    i += 1;
    while (i < lines.length) {
      const next = lines[i];
      if (!next.trim()) {
        break;
      }
      if (
        /^(#{1,6})\s+/.test(next.trim()) ||
        next.trim().startsWith('> ') ||
        /^\d+\.\s+/.test(next.trim()) ||
        /^[-*]\s+/.test(next.trim())
      ) {
        break;
      }
      if (next.endsWith('  ')) {
        paragraphLines.push(next.trimEnd().replace(/ {2}$/, ''));
        blocks.push({
          type: 'paragraph',
          children: parseInline(paragraphLines.join('\n')),
        });
        blocks.push({ type: 'break' });
        i += 1;
        paragraphLines.length = 0;
        continue;
      }
      paragraphLines.push(next.trim());
      i += 1;
    }
    if (paragraphLines.length > 0) {
      blocks.push({
        type: 'paragraph',
        children: parseInline(paragraphLines.join('\n')),
      });
    }
  }

  return blocks;
}

const headingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

const AiMarkdown: React.FC<{ content?: string | null }> = ({ content }) => {
  const markdown = (content || '').trim();
  if (!markdown) {
    return null;
  }

  const blocks = parseBlocks(markdown);
  return (
    <div className="ai-markdown">
      {blocks.map((block, index) => {
        const key = `b-${index}`;
        if (block.type === 'heading') {
          const Tag = headingTags[Math.min(block.level, 6) - 1];
          return <Tag key={key}>{renderInline(block.children, key)}</Tag>;
        }
        if (block.type === 'blockquote') {
          return <blockquote key={key}>{renderInline(block.children, key)}</blockquote>;
        }
        if (block.type === 'list') {
          const ListTag = block.ordered ? 'ol' : 'ul';
          return (
            <ListTag key={key}>
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>
              ))}
            </ListTag>
          );
        }
        if (block.type === 'table') {
          return (
            <table key={key}>
              <thead>
                <tr>
                  {block.headers.map((cell, cellIndex) => (
                    <th key={`${key}-h-${cellIndex}`}>{renderInline(cell, `${key}-h-${cellIndex}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={`${key}-r-${rowIndex}`}>
                    {row.map((cell, cellIndex) => (
                      <td key={`${key}-r-${rowIndex}-${cellIndex}`}>
                        {renderInline(cell, `${key}-r-${rowIndex}-${cellIndex}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
        if (block.type === 'break') {
          return <br key={key} />;
        }
        return <p key={key}>{renderInline(block.children, key)}</p>;
      })}
    </div>
  );
};

export default AiMarkdown;
